import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Autocomplete, LoadScript } from "@react-google-maps/api";
import PropTypes from "prop-types";

const GOOGLE_MAPS_LIBRARIES = ['places'];
const AUTOCOMPLETE_DEBOUNCE_MS = 180;
const MIN_QUERY_LENGTH = 2;
const LOCATION_PRIMARY_TYPES = ['(regions)'];

function getDisplayLocation(placeholder, value) {
  if (typeof value === 'string' && value.length > 0) {
    return value;
  }

  if (placeholder && placeholder.city && placeholder.state) {
    return `${placeholder.city}, ${placeholder.state}`;
  }

  return '';
}

function toLatLngLiteral(location) {
  if (!location?.lat || !location?.lng) {
    return null;
  }

  return {
    lat: Number(location.lat),
    lng: Number(location.lng)
  };
}

function toLegacyPlace(place, fallbackText) {
  return {
    address_components: (place.addressComponents || []).map(component => ({
      long_name: component.longText || '',
      short_name: component.shortText || component.longText || '',
      types: component.types || []
    })),
    formatted_address: place.formattedAddress || fallbackText,
    geometry: {
      location: {
        lat: () => place.location?.lat?.() ?? null,
        lng: () => place.location?.lng?.() ?? null
      }
    }
  };
}

function formatPredictionText(formattableText) {
  return formattableText?.text || '';
}

function shouldFallbackToLegacyAutocomplete(error) {
  const message = String(error?.message || error || '');

  return (
    message.includes('Places API (New) has not been used') ||
    message.includes('AutocompletePlaces') ||
    message.includes('places.googleapis.com') ||
    message.includes('403')
  );
}

const PlaceComponent = ({ value, onChange = () => {}, placeholder, disabled, onPlaceChanged, handlePlaceChanged }) => {
  const wrapperRef = useRef(null);
  const legacyAutocompleteRef = useRef(null);
  const requestIdRef = useRef(0);
  const sessionTokenRef = useRef(null);
  const [inputValue, setInputValue] = useState(() => getDisplayLocation(placeholder, value));
  const [placesLibrary, setPlacesLibrary] = useState(null);
  const [suggestions, setSuggestions] = useState([]);
  const [isOpen, setIsOpen] = useState(false);
  const [activeIndex, setActiveIndex] = useState(-1);
  const [isLoading, setIsLoading] = useState(false);
  const [hasFocus, setHasFocus] = useState(false);
  const [autocompleteMode, setAutocompleteMode] = useState('new');

  const googleMapsApiKey = process.env.REACT_APP_GOOGLE_MAPS_API_KEY;
  const locationBias = useMemo(() => toLatLngLiteral(placeholder), [placeholder]);

  useEffect(() => {
    setInputValue(getDisplayLocation(placeholder, value));
  }, [placeholder, value]);

  useEffect(() => {
    const handlePointerDown = (event) => {
      if (!wrapperRef.current?.contains(event.target)) {
        setIsOpen(false);
        setActiveIndex(-1);
        setHasFocus(false);
      }
    };

    document.addEventListener('mousedown', handlePointerDown);
    document.addEventListener('touchstart', handlePointerDown);

    return () => {
      document.removeEventListener('mousedown', handlePointerDown);
      document.removeEventListener('touchstart', handlePointerDown);
    };
  }, []);

  const initializePlacesLibrary = useCallback(async () => {
    if (!window.google?.maps?.importLibrary) {
      return;
    }

    try {
      const importedLibrary = await window.google.maps.importLibrary('places');
      setPlacesLibrary(importedLibrary);
    } catch (error) {
      console.error('Unable to load Google Places library:', error);
    }
  }, []);

  useEffect(() => {
    if (!placesLibrary || disabled || autocompleteMode !== 'new') {
      return undefined;
    }

    const query = inputValue.trim();

    if (query.length < MIN_QUERY_LENGTH) {
      setSuggestions([]);
      setIsOpen(false);
      setActiveIndex(-1);
      setIsLoading(false);
      return undefined;
    }

    const timeoutId = window.setTimeout(async () => {
      try {
        setIsLoading(true);

        if (!sessionTokenRef.current) {
          sessionTokenRef.current = new placesLibrary.AutocompleteSessionToken();
        }

        const currentRequestId = requestIdRef.current + 1;
        requestIdRef.current = currentRequestId;
        const inputOffset = Math.max(0, query.length - 1);

        const request = {
          input: query,
          inputOffset,
          language: 'en',
          region: 'us',
          includedPrimaryTypes: LOCATION_PRIMARY_TYPES,
          sessionToken: sessionTokenRef.current,
          includedRegionCodes: ['us']
        };

        if (locationBias) {
          request.locationBias = locationBias;
          request.origin = locationBias;
        }

        const response = await placesLibrary.AutocompleteSuggestion.fetchAutocompleteSuggestions(request);

        if (requestIdRef.current !== currentRequestId) {
          return;
        }

        const nextSuggestions = (response?.suggestions || []).filter(
          suggestion => suggestion?.placePrediction
        );

        setSuggestions(nextSuggestions);
        setIsOpen(hasFocus && nextSuggestions.length > 0);
        setActiveIndex(nextSuggestions.length > 0 ? 0 : -1);
      } catch (error) {
        console.error('Unable to fetch autocomplete suggestions:', error);
        if (shouldFallbackToLegacyAutocomplete(error)) {
          setAutocompleteMode('legacy');
        }
        setSuggestions([]);
        setIsOpen(false);
        setActiveIndex(-1);
      } finally {
        setIsLoading(false);
      }
    }, AUTOCOMPLETE_DEBOUNCE_MS);

    return () => {
      window.clearTimeout(timeoutId);
    };
  }, [autocompleteMode, disabled, hasFocus, inputValue, locationBias, placesLibrary]);

  const handleLegacyAutocompleteChange = useCallback((autocompleteRef) => {
    if (!autocompleteRef?.getPlace) {
      return;
    }

    const place = autocompleteRef.getPlace();

    if (!place?.address_components?.length) {
      return;
    }

    setInputValue(place.formatted_address || getDisplayLocation(placeholder, value));
    setSuggestions([]);
    setIsOpen(false);
    setActiveIndex(-1);
    setHasFocus(false);

    if (onPlaceChanged) {
      onPlaceChanged(autocompleteRef);
    }

    if (handlePlaceChanged) {
      handlePlaceChanged(autocompleteRef);
    }
  }, [handlePlaceChanged, onPlaceChanged, placeholder, value]);

  const selectSuggestion = useCallback(async (suggestion) => {
    const prediction = suggestion?.placePrediction;
    if (!prediction) {
      return;
    }

    try {
      const place = prediction.toPlace();
      await place.fetchFields({
        fields: ['addressComponents', 'displayName', 'formattedAddress', 'location']
      });

      const legacyPlace = toLegacyPlace(place, prediction.text?.text || inputValue);
      const autocompleteAdapter = {
        getPlace: () => legacyPlace
      };

      setInputValue(legacyPlace.formatted_address || prediction.text?.text || '');
      setSuggestions([]);
      setIsOpen(false);
      setActiveIndex(-1);
      setHasFocus(false);

      if (onPlaceChanged) {
        onPlaceChanged(autocompleteAdapter);
      }

      if (handlePlaceChanged) {
        handlePlaceChanged(autocompleteAdapter);
      }

      if (placesLibrary?.AutocompleteSessionToken) {
        sessionTokenRef.current = new placesLibrary.AutocompleteSessionToken();
      }
    } catch (error) {
      console.error('Unable to fetch selected place details:', error);
    }
  }, [handlePlaceChanged, inputValue, onPlaceChanged, placesLibrary]);

  const handleInputChange = (event) => {
    setInputValue(event.target.value);
    setHasFocus(true);
    onChange(event);
  };

  const handleKeyDown = (event) => {
    if (!isOpen || suggestions.length === 0) {
      if (event.key === 'ArrowDown' && suggestions.length > 0) {
        event.preventDefault();
        setIsOpen(true);
        setActiveIndex(0);
      }
      return;
    }

    if (event.key === 'ArrowDown') {
      event.preventDefault();
      setActiveIndex((activeIndex + 1) % suggestions.length);
      return;
    }

    if (event.key === 'ArrowUp') {
      event.preventDefault();
      setActiveIndex((activeIndex - 1 + suggestions.length) % suggestions.length);
      return;
    }

    if (event.key === 'Enter' && activeIndex >= 0) {
      event.preventDefault();
      selectSuggestion(suggestions[activeIndex]);
      return;
    }

    if (event.key === 'Escape') {
      setIsOpen(false);
      setActiveIndex(-1);
    }
  };

  const renderTextInput = (extraProps = {}) => (
    <input
      type="text"
      className="store-locator-location-input"
      name="store-locator-location"
      placeholder="Search city or ZIP code"
      autoComplete="off"
      autoCorrect="off"
      autoCapitalize="none"
      spellCheck={false}
      data-lpignore="true"
      data-1p-ignore="true"
      aria-label="Location"
      aria-autocomplete="list"
      aria-expanded={autocompleteMode === 'new' ? isOpen : false}
      aria-controls="place-autocomplete-listbox"
      value={inputValue}
      onChange={handleInputChange}
      onFocus={() => {
        setHasFocus(true);
        if (autocompleteMode === 'new') {
          setIsOpen(suggestions.length > 0);
        }
      }}
      onKeyDown={handleKeyDown}
      disabled={disabled}
      {...extraProps}
    />
  );

  const renderInput = () => (
    <div
      ref={wrapperRef}
      className={`place-autocomplete ${isOpen ? 'is-open' : ''} ${disabled ? 'is-disabled' : ''}`}
    >
      <div className="place-autocomplete__shell">
        <div className="place-autocomplete__icon" aria-hidden="true">
          <svg viewBox="0 0 24 24" focusable="false">
            <path d="M10.5 3a7.5 7.5 0 1 1 0 15 7.5 7.5 0 0 1 0-15Zm0 2a5.5 5.5 0 1 0 3.89 9.39A5.5 5.5 0 0 0 10.5 5Zm8.85 13.44 1.41 1.41-3.87-3.87-1.41-1.41-.02-.02 1.41-1.41.02.02 1.41 1.41 3.06 3.06Z" />
          </svg>
        </div>
        {autocompleteMode === 'legacy' ? (
          <Autocomplete
            onLoad={(ref) => {
              legacyAutocompleteRef.current = ref;
            }}
            onPlaceChanged={() => handleLegacyAutocompleteChange(legacyAutocompleteRef.current)}
            options={{
              componentRestrictions: { country: 'us' },
              fields: ['address_components', 'formatted_address', 'geometry.location']
              ,
              types: ['(regions)']
            }}
          >
            {renderTextInput()}
          </Autocomplete>
        ) : (
          renderTextInput()
        )}
        {isLoading ? (
          <div className="place-autocomplete__status" aria-hidden="true">
            <span />
            <span />
            <span />
          </div>
        ) : null}
      </div>

      {autocompleteMode === 'new' && isOpen && suggestions.length > 0 ? (
        <div className="place-autocomplete__panel">
          <div className="place-autocomplete__label">Choose a location</div>
          <div
            id="place-autocomplete-listbox"
            className="place-autocomplete__list"
            role="listbox"
          >
            {suggestions.map((suggestion, index) => {
              const prediction = suggestion.placePrediction;
              const primaryText = formatPredictionText(prediction?.mainText || prediction?.text);
              const secondaryText = formatPredictionText(prediction?.secondaryText);

              return (
                <button
                  key={`${prediction?.placeId || primaryText}-${index}`}
                  type="button"
                  role="option"
                  aria-selected={index === activeIndex}
                  className={`place-autocomplete__option ${index === activeIndex ? 'is-active' : ''}`}
                  onMouseEnter={() => setActiveIndex(index)}
                  onMouseDown={(event) => {
                    event.preventDefault();
                    selectSuggestion(suggestion);
                  }}
                >
                  <span className="place-autocomplete__option-icon" aria-hidden="true">
                    <svg viewBox="0 0 24 24" focusable="false">
                      <path d="M12 2.75a6 6 0 0 1 6 6c0 4.23-4.58 10.55-5.1 11.25a1.12 1.12 0 0 1-1.8 0C10.58 19.3 6 12.98 6 8.75a6 6 0 0 1 6-6Zm0 2a4 4 0 0 0-4 4c0 2.49 2.39 6.6 4 8.93 1.61-2.34 4-6.44 4-8.93a4 4 0 0 0-4-4Zm0 2.25a1.75 1.75 0 1 1 0 3.5 1.75 1.75 0 0 1 0-3.5Z" />
                    </svg>
                  </span>
                  <span className="place-autocomplete__option-copy">
                    <span className="place-autocomplete__option-main">{primaryText}</span>
                    {secondaryText ? (
                      <span className="place-autocomplete__option-secondary">{secondaryText}</span>
                    ) : null}
                  </span>
                </button>
              );
            })}
          </div>
          <div className="place-autocomplete__powered-by">Powered by Google</div>
        </div>
      ) : null}
    </div>
  );

  if (!googleMapsApiKey) {
    return renderInput();
  }

  return (
    <LoadScript
      googleMapsApiKey={googleMapsApiKey}
      libraries={GOOGLE_MAPS_LIBRARIES}
      preventGoogleFontsLoading
      onLoad={initializePlacesLibrary}
      onError={() => {
        console.error('Unable to load Google Maps JavaScript API');
      }}
    >
      {renderInput()}
    </LoadScript>
  );
};

PlaceComponent.propTypes = {
  value: PropTypes.string,
  onChange: PropTypes.func,
  placeholder: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.shape({
      city: PropTypes.string,
      state: PropTypes.string,
      lat: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
      lng: PropTypes.oneOfType([PropTypes.number, PropTypes.string])
    })
  ]),
  disabled: PropTypes.bool,
  onPlaceChanged: PropTypes.func,
  handlePlaceChanged: PropTypes.func
};

export default PlaceComponent;
